import {
    Component,
    OnInit,
    Input,
    ChangeDetectorRef,
    Renderer2,
    NgZone,
    ViewChildren,
    QueryList,
    ElementRef,
    ViewChild,
    Output,
    EventEmitter,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { Course, Episode as EpisodeLibrary } from '@app/core/model/library.model';
import { ByocService } from '../../../../service/byoc.service';
import { ByocEpisodeService } from '../../../service/episode.service';
import { PerfectScrollbarDirective } from '@app/shared/perfect-scrollbar/perfect-scrollbar.directive';

interface Episode extends EpisodeLibrary {
    created?: boolean;
}

interface Coordinates {
    height: number | null;
    width: number | null;
    top: number | null;
    left: number | null;
    marginTop?: number | null;
    marginLeft?: number | null;
}

@Component({
    selector: 'app-byoc-episode-step-position-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ByocEpisodeStepPositionListComponent implements OnInit {
    @Output() loadedList: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('ps', { read: PerfectScrollbarDirective })
    psRef?: PerfectScrollbarDirective;

    /**
     * list of droppable elements
     */
    @ViewChildren('element') elements!: QueryList<ElementRef>;

    @Input()
    course!: Course;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly byocService = inject(ByocService);
    private readonly byocEpisodeService = inject(ByocEpisodeService);
    private readonly renderer2 = inject(Renderer2);
    private readonly ngZone = inject(NgZone);

    /**
     * List of Episodes with new episode
     */
    episodes: (Episode | { name: string; type: string; created: true })[] = [];

    /**
     * Dragged Node
     */
    node!: Node | null;

    /**
     * Dragged Node List position
     */
    position!: number;

    /**
     * Dragged Node List position
     */
    elementsPosition!: Coordinates[];

    /**
     * Dragged Node List position
     */
    draggedPosition: number | null = null;

    /**
     * coordinates of dragged element
     */
    coordinates!: Coordinates;

    /**
     * Loaded flat
     */
    loaded = false;

    private subscriptions: Subscription = new Subscription();

    ngOnInit() {
        this.subscriptions.add(
            this.byocService.courseEpisodes(<string>this.byocEpisodeService.form.value.courseId).subscribe((episode: Episode[]) => {
                this.episodes = episode;
                this.addCreatedEpisode();
                this.loaded = true;
                this.changeDetectorRef.detectChanges();
                this.setPosition();
                this.psRef?.scrollToBottom();
                this.changeDetectorRef.detectChanges();
                this.loadedList.emit(true);
            }),
        );
    }

    /**
     * Drag Start
     */
    onDragStart(event: DragEvent) {
        this.ngZone.runOutsideAngular(() => {
            window.document.addEventListener('dragover', this.onDrag, false);
        });

        this.startGhost(event, <HTMLElement>event.target);
    }

    /**
     * Drag End
     */
    onDragEnd(event: DragEvent) {
        this.ngZone.runOutsideAngular(() => {
            window.document.removeEventListener('dragover', this.onDrag, false);
        });

        this.endGhost(<HTMLElement>event.target);
    }

    /**
     * On Drag
     */
    private onDrag = (event: MouseEvent): void => {
        event.preventDefault();
        this.coordinates.top = event.clientY;
        this.coordinates.left = event.clientX;
        this.renderer2.setStyle(this.node, 'top', event.clientY.toString() + 'px');
        this.renderer2.setStyle(this.node, 'left', event.clientX.toString() + 'px');
        this.checkPositions();
    };

    /**
     * Adds created episode to the list of course episodes
     */
    private addCreatedEpisode(): void {
        this.episodes.push({
            name: <string>this.byocEpisodeService.form.value.title,
            type: <string>this.byocEpisodeService.form.value.type,
            created: true,
        });
    }

    /**
     * Set Ghost
     */
    private startGhost(event: DragEvent, el: HTMLElement) {
        // start animation
        this.elements.forEach((element: ElementRef) => {
            this.renderer2.addClass(element.nativeElement, 'animated');
        });

        // get start position of dragged element
        this.position = this.episodes.findIndex((episode) => {
            return episode.created;
        });

        // elements position
        this.elementsPosition = this.elements.map((element: ElementRef) => {
            return {
                height: element.nativeElement.getBoundingClientRect().height + 2,
                width: element.nativeElement.getBoundingClientRect().width + 2,
                top: element.nativeElement.getBoundingClientRect().y,
                left: element.nativeElement.getBoundingClientRect().x,
            };
        });

        // clone node
        this.node = el.cloneNode(true);

        // "remove" element
        this.renderer2.setStyle(el, 'opacity', '0.5');

        // set coordinats
        this.coordinates = {
            top: -el.offsetHeight,
            left: -el.offsetWidth,
            marginTop: -event.offsetY,
            marginLeft: -event.offsetX,
            width: el.offsetWidth,
            height: el.offsetHeight,
        };

        // style
        this.renderer2.addClass(this.node, 'attached-to-pointer');
        this.renderer2.setStyle(this.node, 'margin-left', -event.offsetX + 'px');
        this.renderer2.setStyle(this.node, 'margin-top', -event.offsetY + 'px');
        this.renderer2.setStyle(this.node, 'height', el.offsetHeight.toString() + 'px');
        this.renderer2.setStyle(this.node, 'width', el.offsetWidth.toString() + 'px');
        this.renderer2.setStyle(this.node, 'top', -el.offsetHeight.toString() + 'px');
        this.renderer2.setStyle(this.node, 'left', -el.offsetWidth.toString() + 'px');
        // append cloned visible node
        this.renderer2.appendChild(document.querySelector('body'), this.node);
    }

    /**
     * End Ghost
     */
    private endGhost(el: HTMLElement) {
        // end animation
        this.elements.forEach((element: ElementRef) => {
            this.renderer2.removeClass(element.nativeElement, 'animated');
        });

        // render
        this.changeDetectorRef.detectChanges();

        // move element
        if (this.draggedPosition !== null) {
            this.episodes.splice(this.draggedPosition, 0, this.episodes.splice(this.position, 1)[0]);
        }

        // show element
        this.renderer2.setStyle(el, 'opacity', '1');
        this.renderer2.removeChild(document.querySelector('body'), this.node);

        // remove transform
        this.elements.forEach((element: ElementRef) => {
            this.renderer2.removeStyle(element.nativeElement, 'transform');
        });

        this.node = null;
        this.coordinates = {
            top: null,
            left: null,
            marginTop: null,
            marginLeft: null,
            width: null,
            height: null,
        };

        this.elementsPosition = [];
        this.setPosition();
    }

    /**
     * Check dragged element position compare to the rest of elements
     */
    private checkPositions(): void {
        let newIndex = null;

        // get index of hovered element
        this.elements.forEach((el: ElementRef, i: number) => {
            if (i === this.position) {
                return;
            }
            if (this.isNodeAbove(el, i)) {
                newIndex = i;
            }
        });

        this.draggedPosition = newIndex;

        let shift = 0;

        // set styles
        this.elements.forEach((el: ElementRef, i: number) => {
            if (this.draggedPosition === null) {
                this.renderer2.removeStyle(el.nativeElement, 'transform');
            } else if (i >= this.draggedPosition && i < this.position) {
                this.renderer2.setStyle(el.nativeElement, 'transform', 'translateY(60px)');
                shift--;
            } else if (i <= this.draggedPosition && i > this.position) {
                this.renderer2.setStyle(el.nativeElement, 'transform', 'translateY(-60px)');
                shift++;
            } else {
                this.renderer2.removeStyle(el.nativeElement, 'transform');
            }
        });

        // set styles
        this.elements.forEach((el: ElementRef, i: number) => {
            if (i === this.position) {
                this.renderer2.setStyle(el.nativeElement, 'transform', 'translateY(' + shift * 60 + 'px)');
            }
        });
    }

    /**
     * Check if dragged element is above
     */
    private isNodeAbove(el: ElementRef, i: number): boolean {
        // get middle top position of dragged element
        const nodeMiddleTop = <number>this.coordinates.top + <number>this.coordinates.marginTop + <number>this.coordinates.height / 2;
        // get middle top position of dragged element
        const nodeMiddleLeft = <number>this.coordinates.left + <number>this.coordinates.marginLeft + <number>this.coordinates.width / 2;
        // get coordinates of element
        const elCoordinates = this.elementsPosition[i];

        // check if height in range
        if (
            nodeMiddleTop > <number>elCoordinates.top &&
            nodeMiddleTop < <number>elCoordinates.top + <number>elCoordinates.height &&
            nodeMiddleLeft > <number>elCoordinates.left &&
            nodeMiddleLeft < <number>elCoordinates.left + <number>elCoordinates.width
        ) {
            return true;
        }

        return false;
    }

    elementDataTestEspisode(episode: Episode | { name: string; type: string; created: true }): string {
        if ('id' in episode) {
            return episode.id;
        }
        return '';
    }

    /**
     * Set Position
     */
    private setPosition(): void {
        // get position
        this.position = this.episodes.findIndex((episode) => {
            return episode.created;
        });

        this.byocEpisodeService.form.patchValue({
            position: this.position,
        });
    }
}
